
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Download, Upload, RefreshCw, CheckCircle, List, FileArchive } from 'lucide-react';
import { toast } from 'sonner';
import { createPaymentBackup, restorePaymentBackup } from '@/services/backup/paymentBackup';
import { motion } from 'framer-motion';
import PaymentBackupList from './PaymentBackupList';

const PaymentBackupPanel = () => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [showBackupList, setShowBackupList] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const [stats, setStats] = useState({
    teacherPayments: 0,
    studentPayments: 0
  });
  
  useEffect(() => {
    refreshPaymentStats();
  }, []);
  
  const refreshPaymentStats = () => {
    // Count teacher payments
    const teacherKeys = Object.keys(localStorage).filter(key => key.startsWith('teacher_payments_'));
    let totalTeacherPayments = 0;
    teacherKeys.forEach(key => {
      try {
        const payments = JSON.parse(localStorage.getItem(key) || '[]');
        totalTeacherPayments += payments.length;
      } catch (error) {
        console.warn(`Error parsing teacher payments for ${key}:`, error);
      }
    });
    
    // Count student payments
    const studentKeys = Object.keys(localStorage).filter(key => key.startsWith('student_payments_'));
    let totalStudentPayments = 0;
    studentKeys.forEach(key => {
      try {
        const payments = JSON.parse(localStorage.getItem(key) || '[]');
        totalStudentPayments += payments.length;
      } catch (error) {
        console.warn(`Error parsing student payments for ${key}:`, error);
      }
    });
    
    // Also count individual payment status changes
    const paymentStatusChanges = localStorage.getItem('nalc_payment_status_changes');
    if (paymentStatusChanges) {
      try {
        const changes = JSON.parse(paymentStatusChanges);
        if (Array.isArray(changes)) {
          totalStudentPayments += changes.length;
        }
      } catch (error) {
        console.warn('Error parsing payment status changes:', error);
      }
    }
    
    setStats({
      teacherPayments: totalTeacherPayments,
      studentPayments: totalStudentPayments
    });
  };
  
  const handleCreateBackup = async () => {
    if (stats.teacherPayments === 0 && stats.studentPayments === 0) {
      toast.warning('No payment data to backup');
      return;
    }
    
    setIsCreatingBackup(true);
    setBackupCreated(false);
    
    try {
      await createPaymentBackup();
      setBackupCreated(true);
      setTimeout(() => setBackupCreated(false), 3000);
      // Refresh payment stats after backup
      refreshPaymentStats();
    } catch (error) {
      console.error('Failed to create payment backup:', error);
      toast.error('Failed to create payment backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsRestoringBackup(true);
    
    try {
      const result = await restorePaymentBackup(file);
      if (result) {
        toast.success('Payment data restored successfully');
        // Refresh payment stats after restoration
        refreshPaymentStats();
      }
    } catch (error) {
      console.error('Failed to restore payment backup:', error);
      toast.error('Failed to restore payment backup');
    } finally {
      setIsRestoringBackup(false);
      // Clear the input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const toggleBackupList = () => {
    setShowBackupList(!showBackupList);
    if (!showBackupList) {
      // Refresh payment stats when showing the backup list
      refreshPaymentStats();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <FileArchive className="h-5 w-5 mr-2 text-primary" />
          Payment & Fee Backup System
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          {stats.teacherPayments + stats.studentPayments === 0 
            ? 'No payment data available for backup' 
            : `${stats.teacherPayments + stats.studentPayments} payment records available for backup`}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-2 gap-2 mb-4 text-xs text-muted-foreground">
          <div className="flex items-center">
            <span>Teacher Payments: {stats.teacherPayments}</span>
          </div>
          <div className="flex items-center">
            <span>Student Payments: {stats.studentPayments}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleCreateBackup}
              disabled={isCreatingBackup || (stats.teacherPayments + stats.studentPayments === 0)}
              variant={backupCreated ? "secondary" : "default"}
            >
              {isCreatingBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Creating Backup...</span>
                </>
              ) : backupCreated ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>Backup Created!</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  <span>Create Payment Backup</span>
                </>
              )}
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleSelectFile}
              disabled={isRestoringBackup}
              variant="outline"
            >
              {isRestoringBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Restoring Backup...</span>
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  <span>Restore from File</span>
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json"
                className="hidden"
              />
            </Button>
          </motion.div>
        </div>
        
        <Button
          variant="ghost"
          className="w-full mt-2"
          onClick={toggleBackupList}
        >
          <List className="h-4 w-4 mr-2" />
          {showBackupList ? "Hide Backup History" : "View Backup History"}
        </Button>
        
        {showBackupList && (
          <PaymentBackupList onBackupRestored={refreshPaymentStats} />
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentBackupPanel;
